/**
 * The loyalty programme, front-end only.
 *
 * WHY IT LIVES HERE. `menu_core_be` has no loyalty domain at all: no points,
 * no rewards, no redemptions. The RMS says so itself in
 * `features/loyalty/config.ts`: "This is not 'the endpoint is not wired yet',
 * it is 'the endpoint does not exist'". The programme a restaurant configures
 * in the RMS is held in that browser's own localStorage, so it cannot reach a
 * guest's phone. Until an endpoint exists, these constants stand in for it.
 *
 * Everything below mirrors the RMS's own model and defaults, so swapping this
 * file for a fetch is a straight substitution rather than a redesign.
 */

/** Points granted per whole major unit spent, e.g. 1 point per 1 zł. */
export const POINTS_PER_MAJOR_UNIT = 1;

/** Bills below this earn nothing. Minor units (grosze), as everywhere else. */
export const MINIMUM_BILL_GROSZE = 2000;

/**
 * Unused points expire this long after the guest's last visit, rather than
 * after they were earned, so every visit renews the whole balance. Fixed
 * rather than configurable, exactly as the RMS has it: it is a promise made
 * at the till.
 */
export const POINTS_VALIDITY_MONTHS = 12;

/** What a first reward tends to cost. The RMS phrases its hint around this. */
export const TYPICAL_REWARD_POINTS = 200;

/**
 * A reward a guest can exchange points for. Same shape as the RMS's
 * LoyaltyReward: dishes are referenced by id so a renamed dish renames here
 * too, and several ids mean the guest picks one of them.
 */
export interface LoyaltyRewardConfig {
  id: string;
  dishIds: string[];
  points: number;
  isActive: boolean;
}

/**
 * Rewards this restaurant offers.
 *
 * Empty on purpose: dish ids differ per restaurant, so anything seeded here
 * would be wrong everywhere except one venue. Leave it empty and the guest
 * screen falls back to REWARD_FALLBACK below, which builds a list from the
 * live menu instead of inventing dishes that do not exist.
 *
 * To pin real rewards, add entries with ids taken from the menu:
 *   { id: "r1", dishIds: ["<dishId>"], points: 200, isActive: true }
 */
export const CONFIGURED_REWARDS: LoyaltyRewardConfig[] = [];

/**
 * Used only while CONFIGURED_REWARDS is empty: offer the cheapest dishes on
 * the menu, each at the typical reward cost. Derived from real menu data, so
 * every reward shown is a dish the kitchen actually makes.
 */
export const REWARD_FALLBACK = {
  count: 3,
  /**
   * Deliberately low, so the programme can be exercised end to end: one normal
   * order earns enough to claim a reward. TYPICAL_REWARD_POINTS (200) is what
   * a real programme charges, and is the number to restore here once the
   * RMS programme is readable from an endpoint.
   */
  points: 20,
};

/**
 * How long a redemption code stays valid once the guest shows it.
 *
 * Points are reserved for that window rather than spent: the guest can open
 * the code, put the phone away and come back to it. If the waiter never hands
 * the reward over, the reservation lapses and the points return by themselves.
 */
export const REDEMPTION_VALIDITY_MINUTES = 30;

/**
 * A one-off bonus for a guest who leaves an e-mail or a phone number after
 * paying. Zero means the guest is never asked, which is the RMS default and
 * the honest one: the programme's whole pitch is that it needs no contact
 * details, so collecting them is a decision a venue makes deliberately.
 *
 * Non-zero here only because the prompt is otherwise invisible. Note what is
 * NOT solved by this constant: the contact is kept in this browser and goes
 * nowhere, because there is no endpoint for it, and no consent record, no
 * retention period and no way to honour a deletion request exist yet. Do not
 * treat what this collects as a contact base.
 */
export const CONTACT_BONUS_POINTS = 50;
